/** Fuel-gap readout: how far apart this route's Fuel waypoints (and the
 * route's own start/end) sit along the trail, so a long dry stretch shows
 * up on the stats bar before it becomes a problem on the ground. Pure — no
 * rendering, no I/O; same split as daystats.ts. */

import { cumulativeM } from "./geo";
import { planGeometry, type LonLat, type Plan } from "./types";

/** Local equirectangular scale for nearest-vertex projection below — the
 * exact kx/ky idiom plannerCore.ts's localScale uses for its own map
 * hit-testing (111320·cos(lat) for longitude, a flat 110540 for latitude):
 * plenty accurate at route-planning precision, no need for true geodesics.
 * Repeated here rather than imported from plannerCore.ts, which is App's
 * map hit-testing layer, not a shared geometry util — not worth a
 * cross-module dependency for two lines of math. */
function localScale(lat: number): { kx: number; ky: number } {
  return { kx: 111320 * Math.cos((lat * Math.PI) / 180), ky: 110540 };
}

/** Index of the flattened route vertex nearest `at`, by flat-earth
 * distance (localScale above) — a nearest VERTEX, not plannerCore's
 * nearestOnLeg point-to-SEGMENT projection: a Fuel waypoint is dropped at
 * (or immediately next to) a point already on the drawn route, so snapping
 * to the closest existing vertex is both simpler and accurate enough for a
 * gap READOUT, unlike click hit-testing, which needs segment precision. */
function nearestVertexIndex(geometry: LonLat[], at: LonLat): number {
  const { kx, ky } = localScale(at[1]);
  let best = 0;
  let bestD2 = Infinity;
  for (let i = 0; i < geometry.length; i++) {
    const dx = (geometry[i][0] - at[0]) * kx;
    const dy = (geometry[i][1] - at[1]) * ky;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = i;
    }
  }
  return best;
}

export interface FuelGaps {
  maxGapM: number;
  /** start->first stop, between each consecutive pair of stops, last
   * stop->route end — sorted by along-route distance. Length = (number of
   * Fuel waypoints) + 1. */
  gaps: number[];
}

/** Gaps between this plan's Fuel waypoints (phone vocabulary: any waypoint
 * whose name starts "Fuel" — types.ts's Waypoint doc covers the kind-is-
 * the-name convention), projected onto the flattened route and sorted by
 * along-route distance. null when the plan has no Fuel waypoints —
 * nothing to report, same "absent means N/A" contract as PlanStats'
 * elevation fields — or when the route itself has fewer than 2 geometry
 * points (nothing to project onto). */
export function fuelGaps(plan: Pick<Plan, "points" | "legs" | "waypoints">): FuelGaps | null {
  const fuelStops = plan.waypoints.filter((w) => w.name.startsWith("Fuel"));
  if (fuelStops.length === 0) return null;

  const geometry = planGeometry(plan);
  if (geometry.length < 2) return null;

  const cum = cumulativeM(geometry);
  const totalM = cum[cum.length - 1];
  const alongM = fuelStops
    .map((w) => cum[nearestVertexIndex(geometry, w.at)])
    .sort((a, b) => a - b);

  const gaps: number[] = [];
  let prev = 0;
  for (const d of alongM) {
    gaps.push(d - prev);
    prev = d;
  }
  gaps.push(totalM - prev);

  return { maxGapM: Math.max(...gaps), gaps };
}
